import { useEffect, useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import InputText from "./inputText";
import { useTranslations } from "next-intl";
import Textarea from "./textarea";
import RadioGroup from "./radioGroup";
import Checkbox from "./checkbox";
import { m, AnimatePresence } from "framer-motion";
import Slider from "./slider";
import EstimatedQuote from "./estimatedQuote";
import Button from "../button";
import { normalizePhoneToE164 } from "@/app/lib/phone";

export interface FormProps {
  activeStepId: string;
  fields: Array<FieldProps>;
  defaultValues: Record<string, any>;
  showBackButton?: boolean;
  onSubmit(values: Record<string, any>): void;
  onBack(values: Record<string, any>): void;
  isLastStep?: boolean;
  submitting?: boolean;
  progressive?: boolean;
}

export const variantsChildren = {
  initial: { x: 32, opacity: 0 },
  animate: { x: 0, opacity: 1, transition: { type: "spring", duration: 0.5 } },
  exit: { x: -32, opacity: 0, transition: { type: "spring", duration: 0.5 } },
};

export interface FieldProps {
  name: string;
  type:
    | "radio"
    | "checkbox"
    | "text"
    | "email"
    | "slider"
    | "textarea"
    | "tel"
    | "hidden";
  options?: string[];
  defaultValue?: string;
  min?: number;
  max?: number;
  step?: number;
  isCurrency?: boolean;
  required?: boolean;
  title?: string;
}

const progressiveVariants = {
  initial: { height: 0, opacity: 0 },
  animate: {
    height: "auto",
    opacity: 1,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  },
  exit: { height: 0, opacity: 0, transition: { duration: 0.2 } },
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MultiStepForm: React.FC<Readonly<FormProps>> = (props) => {
  const {
    fields,
    defaultValues,
    onSubmit,
    isLastStep,
    showBackButton,
    onBack,
    submitting,
    progressive,
  } = props;

  const t = useTranslations("form");
  const formInstance = useForm({
    defaultValues: useMemo(() => defaultValues, [defaultValues]),
  });
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = formInstance;
  const formValues = useWatch({ control: formInstance.control });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleBack = () => {
    const values = formInstance.getValues();
    onBack(values);
  };

  const allFieldsFilled = progressive
    ? fields.filter((f) => f.type !== "hidden").every((f) => formValues[f.name])
    : true;

  const actionButtons = (
    <div className="flex gap-2 justify-center mt-4">
      {showBackButton && (
        <Button
          type="button"
          onClick={handleBack}
          variant="arrow-reverse"
          className="disabled:pointer-events-none"
          disabled={submitting}
        >
          {t("back")}
        </Button>
      )}

      <Button
        type="submit"
        variant="light"
        className={`btn-arrow flex items-center justify-center gap-2 disabled:pointer-events-none ${submitting ? "submitting" : ""} `}
        disabled={submitting}
      >
        {isLastStep ? t("submit") : t("next")}
      </Button>
    </div>
  );

  return (
    <FormProvider {...formInstance}>
      <form
        noValidate
        onSubmit={handleSubmit(async (data) => {
          await onSubmit(data);
        })}
        className="grid gap-2 max-w-md mx-auto w-full"
      >
        {fields.map((field, index) => {
          // Progressive reveal: show field only if all previous fields have values
          if (progressive && index > 0) {
            const previousFields = fields.slice(0, index);
            const allPreviousFilled = previousFields.every((f) =>
              formValues[f.name],
            );
            if (!allPreviousFilled) return null;
          }

          const fieldContent = (
            <div key={field.name} className="w-full">
              {field.type === "text" ||
              field.type === "email" ||
              field.type === "tel" ? (
                <InputText
                  id={field.name}
                  type={field.type}
                  label={t(`${field.name}Label`)}
                  register={register(field.name, {
                    required: field.required ? t("errorRequired") : false,
                    ...(field.type === "email"
                      ? {
                          pattern: {
                            value: EMAIL_PATTERN,
                            message: t("errorEmail"),
                          },
                        }
                      : {}),
                    ...(field.type === "tel"
                      ? {
                          validate: (value: string) =>
                            !value?.trim() ||
                            normalizePhoneToE164(value) !== null ||
                            t("errorTel"),
                        }
                      : {}),
                  })}
                  error={errors[field.name]?.message}
                />
              ) : field.type === "textarea" ? (
                <Textarea
                  id={field.name}
                  label={t(`${field.name}Label`)}
                  register={register(field.name, {
                    required: field.required ? "*" : false,
                  })}
                  error={errors[field.name]?.message}
                />
              ) : field.type === "radio" ? (
                <RadioGroup
                  id={field.name}
                  label={field.title ?? t(`${field.name}Label`)}
                  register={register(field.name, {
                    required: field.required ? "required" : false,
                  })}
                  options={field.options}
                  defaultValue={field.defaultValue}
                  onSelect={
                    progressive
                      ? (fieldName, value) => {
                          window.dataLayer?.push({
                            event: `form_step_${index + 1}_complete`,
                            form_name: "cta",
                            field_name: fieldName,
                            field_value: value,
                          });
                        }
                      : undefined
                  }
                />
              ) : field.type === "checkbox" ? (
                <Checkbox
                  id={field.name}
                  label={t(`${field.name}Label`)}
                  register={register(field.name, {
                    required: field.required ? "required" : false,
                  })}
                  error={errors[field.name]?.message}
                />
              ) : field.type === "slider" ? (
                <Slider
                  id={field.name}
                  label={t(`${field.name}Label`)}
                  register={register(field.name, { required: "Required" })}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  defaultValue={field.min}
                  isCurrency={field.isCurrency}
                />
              ) : field.type === "hidden" ? (
                <EstimatedQuote />
              ) : null}
            </div>
          );

          if (progressive) {
            return (
              <m.div
                key={field.name}
                variants={progressiveVariants}
                initial="initial"
                animate="animate"
                className="overflow-hidden"
              >
                {fieldContent}
              </m.div>
            );
          }

          return fieldContent;
        })}
        {progressive ? (
          <AnimatePresence initial={false}>
            {allFieldsFilled && (
              <m.div
                key="form-actions"
                variants={progressiveVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="overflow-hidden"
              >
                {actionButtons}
              </m.div>
            )}
          </AnimatePresence>
        ) : (
          allFieldsFilled && actionButtons
        )}
      </form>
    </FormProvider>
  );
};

export default MultiStepForm;
